const applyFlipAndRotation = (ctx, canvasWidth, canvasHeight, transform) => {
    const { rotationDeg = 0, flipH = false, flipV = false } = transform
    ctx.translate(canvasWidth / 2, canvasHeight / 2)
    if (rotationDeg) ctx.rotate((rotationDeg * Math.PI) / 180)
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1)
    ctx.translate(-canvasWidth / 2, -canvasHeight / 2)
}

const getMediaDimensions = (mediaEl) => ({
    width: mediaEl.videoWidth || mediaEl.naturalWidth || mediaEl.width || 0,
    height: mediaEl.videoHeight || mediaEl.naturalHeight || mediaEl.height || 0
})

const coverRect = (canvasWidth, canvasHeight, sourceRatio) => {
    const canvasRatio = canvasWidth / canvasHeight
    let drawWidth
    let drawHeight
    if (sourceRatio > canvasRatio) {
        drawHeight = canvasHeight
        drawWidth = drawHeight * sourceRatio
    } else {
        drawWidth = canvasWidth
        drawHeight = drawWidth / sourceRatio
    }
    return { drawWidth, drawHeight, offsetX: (canvasWidth - drawWidth) / 2, offsetY: (canvasHeight - drawHeight) / 2 }
}

// Fills the canvas behind a letterboxed/pillarboxed clip (own crop ratio different from the
// canvas ratio) - either a flat color, or a blurred/darkened cover-fit copy of the same frame
// ("gradient of the current frame" look popularized by short-form video apps), so the bars never
// read as a plain, jarring empty rectangle.
const drawBackgroundFill = (ctx, mediaEl, canvasSize, srcRect, background) => {
    const { width: canvasWidth, height: canvasHeight } = canvasSize
    if (!background || background.mode !== 'blur') {
        ctx.fillStyle = (background && background.color) || '#000000'
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
        return
    }

    const { sx, sy, sWidth, sHeight } = srcRect
    const { drawWidth, drawHeight, offsetX, offsetY } = coverRect(canvasWidth, canvasHeight, sWidth / sHeight)

    ctx.save()
    ctx.filter = 'blur(40px) brightness(0.55)'
    // Slightly oversized so the filter's own edge bleed never reveals a hairline of unblurred canvas.
    const scale = 1.15
    const extraW = drawWidth * (scale - 1)
    const extraH = drawHeight * (scale - 1)
    ctx.drawImage(mediaEl, sx, sy, sWidth, sHeight, offsetX - extraW / 2, offsetY - extraH / 2, drawWidth * scale, drawHeight * scale)
    ctx.restore()
}

// Draws a single video/image/canvas source, applying transform.cropRect (normalized 0-1 rect in
// the source's own pixel space) then fitting whatever remains into the canvas - "cover" fills the
// canvas (may crop overflow), "contain" fits entirely inside it (may letterbox: the leftover
// space is filled via `background` - solid color or blurred frame - instead of staying transparent).
export const drawMediaFrame = (ctx, mediaEl, canvasSize, transform = {}, { clear = true, fit = 'cover', background = null } = {}) => {
    const { width: canvasWidth, height: canvasHeight } = canvasSize
    const { width: sourceWidth, height: sourceHeight } = getMediaDimensions(mediaEl)
    if (!sourceWidth || !sourceHeight) return

    ctx.save()
    if (clear) ctx.clearRect(0, 0, canvasWidth, canvasHeight)

    const cropRect = transform.cropRect || { x: 0, y: 0, width: 1, height: 1 }
    const sx = cropRect.x * sourceWidth
    const sy = cropRect.y * sourceHeight
    const sWidth = cropRect.width * sourceWidth
    const sHeight = cropRect.height * sourceHeight

    // A rotated rectangle's on-screen bounding box isn't just its own width/height anymore - at
    // 90°/270° it's a plain swap, but any other angle (see the rotation slider in ClipToolPanel.vue)
    // grows *both* axes. Fit/letterbox math has to size against this effective footprint, not the
    // crop's raw dimensions, or a rotated clip either overflows the canvas or leaves gaps the
    // background fill was never told to cover.
    const rotationRad = ((transform.rotationDeg || 0) * Math.PI) / 180
    const absCos = Math.abs(Math.cos(rotationRad))
    const absSin = Math.abs(Math.sin(rotationRad))
    const boundingWidth = sWidth * absCos + sHeight * absSin
    const boundingHeight = sWidth * absSin + sHeight * absCos

    const canvasRatio = canvasWidth / canvasHeight
    const boundingRatio = boundingWidth / boundingHeight
    // 'cover' normally guarantees zero gaps by construction (the bounding box always covers the
    // canvas) - but that's only true for an axis-aligned rectangle. Once actually rotated by
    // anything other than a 90° multiple, the bounding box covering the canvas does NOT mean the
    // rotated rectangle itself does: its own (now diagonal) corners pull inward from the bounding
    // box's corners, leaving small triangular gaps at the canvas's own corners with nothing drawn
    // there at all (previously: bare black canvas, background fill skipped entirely for 'cover').
    const isAxisAlignedRotation = Math.abs((transform.rotationDeg || 0) % 90) < 0.01
    const needsLetterbox = (fit === 'contain' && Math.abs(boundingRatio - canvasRatio) > 0.001) || !isAxisAlignedRotation

    if (needsLetterbox) drawBackgroundFill(ctx, mediaEl, canvasSize, { sx, sy, sWidth, sHeight }, background)

    applyFlipAndRotation(ctx, canvasWidth, canvasHeight, transform)

    const boundingFitsWiderThanCanvas = boundingRatio > canvasRatio
    const scale = (boundingFitsWiderThanCanvas === (fit === 'cover'))
        ? canvasHeight / boundingHeight
        : canvasWidth / boundingWidth

    const drawWidth = sWidth * scale
    const drawHeight = sHeight * scale
    const offsetX = (canvasWidth - drawWidth) / 2
    const offsetY = (canvasHeight - drawHeight) / 2

    ctx.drawImage(mediaEl, sx, sy, sWidth, sHeight, offsetX, offsetY, drawWidth, drawHeight)
    ctx.restore()
}
